import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { startAnalytics, trackAnalytics, trackLegacyView } from '@/lib/analytics';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  ArrowRight,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronRight,
  Search,
  SearchX,
  Gift,
  History,
  Loader2,
  Clock,
  LayoutGrid,
  List,
  FileText,
  Eye,
  Maximize2,
  Sparkles,
  Play,
  SlidersHorizontal,
  Copy,
  FileVideo,
  Languages,
  Mic2,
  Music,
  Presentation,
  MessageCircle,
  LogOut,
  X,
  XCircle,
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import {
  showUserError,
  showUserInfo,
  showUserSuccess,
  showUserToast,
} from '@/utils/user-toast';
import { Image } from '@/components/ui/image';
import { getActiveTopics, verifyPasscode, redeemResource, getMemberByPasscode, trackTopicView } from '@/api/member';
import { getPublicContactConfig } from '@/api/admin';
import { MemberBenefitsDialog } from '@/components/member/MemberBenefitsDialog';
import { MemberHistoryDialog } from '@/components/member/MemberHistoryDialog';
import { OrderNoBindForm } from '@/components/member/OrderNoBindForm';
import { TopicFilterDrawer } from '@/components/topic/TopicFilterDrawer';
import { TOPIC_FILTER_LABELS } from '@/components/topic/topic-filter-copy';
import { CategoryFilterIcon, DurationDialIcon } from '@/components/topic/topic-filter-icons';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
} from '@/components/ui/drawer';
import { useMember } from '@/contexts/MemberContext';
import {
  CONTENT_CATEGORY_LABELS,
  CONTENT_DIRECTION_LABELS,
} from '@shared/api.interface';
import type {
  ResourceTopic,
  ContentCategory,
  ContactConfig,
  MemberVerifyResult,
} from '@shared/api.interface';
import { getCycleDateRange } from '@/utils/cycle-dates';
import {
  DURATION_ALL_VALUE,
  DURATION_FILTER_OPTIONS,
  getDurationRangeLabel,
  isDurationInRanges,
  type DurationRange,
} from '@/utils/topic-duration';
import { PUBLIC_CATALOG_MODE } from '@/config/public-mode';
import { ADMIN_LOGIN_PATH } from '@/config/admin-routes';
import { useIsMobile } from '@/hooks/use-mobile';
import {
  extractMemberErrorMessage,
  getMemberErrorMessage,
  getQuotaExhaustedMessage,
  MEMBER_ERROR_MESSAGES,
} from '@/utils/member-error-message';
import { showQuotaExhaustedToast } from '@/utils/quota-exhausted-toast';
import { isValidPasscode, normalizePasscode } from '@shared/input-validation';
import { isTopicClaimed, useClaimedTopics } from '@/hooks/use-claimed-topics';
import { getCategoryStyle } from '@/utils/topic-category-style';

// 手机端浏览方式：封面三列（默认）或单列列表；记在本机，下次进来保持
type MobileViewMode = 'cover' | 'list';
const VIEW_MODE_KEY = 'public_topic_view_mode';

// 详情“包含资源”：家长最关心背景视频（是否有无人声版）和可编辑 PPT，放大单独说明；其余一行带过
const PRIMARY_RESOURCES = [
  { icon: FileVideo, title: '演讲背景视频', points: ['原声示范', '无人声有字幕', '无人声无字幕'] },
  { icon: Presentation, title: '演讲背景 PPT', points: ['可编辑', '内含背景音乐'] },
];
const SECONDARY_RESOURCES = [
  { icon: Languages, title: '中英双语文稿' },
  { icon: Mic2, title: '示范音频' },
  { icon: Music, title: '背景音乐' },
];

const HIDE_PPT_PREVIEW = false;
const VIDEO_WATERMARK_ITEMS = Array.from({ length: 9 }, (_, index) => `${index}`);

type DetailPreviewKind = 'video' | 'ppt' | 'script';

// 详情大图可切换的预览项，顺序：视频 → PPT → 文稿
function getDetailPreviews(topic: ResourceTopic) {
  const items: Array<{ kind: DetailPreviewKind; label: string; src: string | null }> = [];
  if (topic.video_display) items.push({ kind: 'video', label: '视频', src: topic.cover_image });
  if (!HIDE_PPT_PREVIEW && topic.ppt_display) items.push({ kind: 'ppt', label: 'PPT', src: topic.ppt_display });
  if (topic.script_display) items.push({ kind: 'script', label: '文稿', src: topic.script_display });
  return items;
}
const PROTECTED_PREVIEW_STYLE = {
  WebkitUserSelect: 'none',
  userSelect: 'none',
  WebkitTouchCallout: 'none',
  touchAction: 'manipulation',
} as any;
const DEFAULT_CONTACT_CONFIG: ContactConfig = {
  enabled: false,
  // 登录旁客服入口默认跟随“启用前台客服入口”展示，可独立关闭
  show_header_entry: true,
  method: 'qr',
  title: '购买 / 续费 / 单独获取',
  description: '支持会员开通、续费、单独获取主题、找回兑换码',
  detail_description: '支持兑换码领取、单独获取、续费咨询',
  passcode_hint: '兑换码通常为订单号，需获取可联系客服',
  button_text: '联系客服',
  wechat_id: '',
  qr_image_url: '',
  link_url: '',
};

const resolvePublicAssetUrl = (filename: string): string => {
  if (typeof document === 'undefined') {
    return filename;
  }
  const faviconHref = document
    .querySelector('link[rel="icon"]')
    ?.getAttribute('href')
    ?.trim();
  if (!faviconHref) {
    return filename;
  }

  try {
    const faviconUrl = new URL(faviconHref, window.location.href);
    faviconUrl.pathname = faviconUrl.pathname.replace(
      /favicon\.svg$/i,
      filename,
    );
    faviconUrl.search = '';
    faviconUrl.hash = '';
    return faviconUrl.toString();
  } catch {
    return faviconHref.replace(/favicon\.svg(?:\?.*)?$/i, filename);
  }
};

// 桌面宽屏（≥1024px）详情改为左右双栏宽弹窗
const WIDE_DETAIL_QUERY = '(min-width: 1024px)';
function useIsWideDesktop() {
  const [isWide, setIsWide] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(WIDE_DETAIL_QUERY).matches,
  );
  useEffect(() => {
    const mql = window.matchMedia(WIDE_DETAIL_QUERY);
    const onChange = () => setIsWide(mql.matches);
    onChange();
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);
  return isWide;
}

const PublicTopicListPage = () => {
  const navigate = useNavigate();
  const { session, login, logout, updateQuota, refreshSession } = useMember();
  const [topics, setTopics] = useState<ResourceTopic[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ContentCategory | ''>('');
  const [durationRanges, setDurationRanges] = useState<DurationRange[]>([]);
  const [mobileViewMode, setMobileViewModeState] = useState<MobileViewMode>(() => {
    try {
      return localStorage.getItem(VIEW_MODE_KEY) === 'list' ? 'list' : 'cover';
    } catch {
      return 'cover';
    }
  });
  const setMobileViewMode = (mode: MobileViewMode) => {
    setMobileViewModeState(mode);
    try {
      localStorage.setItem(VIEW_MODE_KEY, mode);
    } catch {
      // 隐私模式下无法保存，仅本次生效
    }
  };
  const isMobileWidth = useIsMobile();
  const isWideDetail = useIsWideDesktop();
  const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);

  // 详情弹窗状态
  const [detailTopic, setDetailTopic] = useState<ResourceTopic | null>(null);
  // 详情大图当前显示哪一项预览：视频 / PPT / 文稿
  const [detailPreviewKind, setDetailPreviewKind] = useState<DetailPreviewKind>('video');
  const galleryRef = useRef<HTMLDivElement | null>(null);
  // 滑动大图时同步高亮对应缩略图
  const handleGalleryScroll = () => {
    const el = galleryRef.current;
    if (!el || !detailTopic || el.clientWidth === 0) return;
    const index = Math.round(el.scrollLeft / el.clientWidth);
    const item = getDetailPreviews(detailTopic)[index];
    if (item && item.kind !== detailPreviewKind) setDetailPreviewKind(item.kind);
  };
  // 点缩略图时把大图滑到对应位置
  const scrollGalleryTo = (index: number, kind: DetailPreviewKind) => {
    setDetailPreviewKind(kind);
    const el = galleryRef.current;
    if (el) el.scrollTo({ left: index * el.clientWidth, behavior: 'smooth' });
  };
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  // 兑换码验证弹窗状态
  const [isPasscodeDialogOpen, setIsPasscodeDialogOpen] = useState(false);
  const [passcodeDialogMode, setPasscodeDialogMode] = useState<'login' | 'redeem'>('redeem');
  const [passcode, setPasscode] = useState('');
  const [passcodeError, setPasscodeError] = useState('');
  // 批量码首次激活后，在兑换码弹窗里多一步选填订单号
  const [activationStep, setActivationStep] = useState<MemberVerifyResult | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [pendingRedeem, setPendingRedeem] = useState<{
    topic: ResourceTopic;
  } | null>(null);

  // 领取确认弹窗状态
  const [confirmTopic, setConfirmTopic] = useState<ResourceTopic | null>(null);

  const [isRedeeming, setIsRedeeming] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [previewVideo, setPreviewVideo] = useState<string | null>(null);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [contactConfig, setContactConfig] = useState<ContactConfig>(DEFAULT_CONTACT_CONFIG);
  const [adminTapCount, setAdminTapCount] = useState(0);
  const brandLogoSrc = resolvePublicAssetUrl('brand-logo.svg');

  // 已领取标记与“只看未领取”
  const claimedTopics = useClaimedTopics(PUBLIC_CATALOG_MODE ? null : session?.passcode);
  const [unclaimedOnly, setUnclaimedOnly] = useState(false);

  // 手机端“我的会员”面板、领取记录（可从卡片“查看资源”带着主题名打开）
  const [isMemberCenterOpen, setIsMemberCenterOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [historyQuery, setHistoryQuery] = useState('');

  const [showBackToTop, setShowBackToTop] = useState(false);
  useEffect(() => {
    if (!session) setUnclaimedOnly(false);
  }, [session]);

  // 从领取成功页点“查看领取记录”回来时，直接打开领取记录；用完即清掉，刷新不会重复打开
  const location = useLocation();
  useEffect(() => {
    const state = location.state as { openHistory?: boolean } | null;
    if (!state?.openHistory) return;
    if (session) {
      setHistoryQuery('');
      setIsHistoryOpen(true);
    }
    navigate(location.pathname, { replace: true, state: null });
  }, [location.state, location.pathname, session, navigate]);

  // 桌面端品牌栏固定在顶部；手机端搜索和分类随页面滚走，分类一滑出屏幕，顶部出现收起后的条件栏
  const brandHeaderRef = useRef<HTMLDivElement | null>(null);
  const mobileCategoryRef = useRef<HTMLDivElement | null>(null);
  const mobileSearchInputRef = useRef<HTMLInputElement | null>(null);
  const compactBarRef = useRef<HTMLDivElement | null>(null);
  const resultsAnchorRef = useRef<HTMLDivElement | null>(null);
  const [showCompactBar, setShowCompactBar] = useState(false);
  const getStickyHeaderHeight = () => {
    const compactBar = compactBarRef.current;
    if (compactBar && window.getComputedStyle(compactBar).display !== 'none') {
      return compactBar.offsetHeight;
    }
    const brandHeader = brandHeaderRef.current;
    if (brandHeader && window.getComputedStyle(brandHeader).position === 'sticky') {
      return brandHeader.offsetHeight;
    }
    return 0;
  };
  useEffect(() => {
    const onScroll = () => {
      setShowBackToTop(window.scrollY > 900);
      const categoryEl = mobileCategoryRef.current;
      const compactBar = compactBarRef.current;
      // 分类区被条件栏盖住时就显示条件栏；只在手机布局下生效（桌面端两个元素都不显示，高度为 0）
      setShowCompactBar(
        !!categoryEl &&
          !!compactBar &&
          categoryEl.offsetHeight > 0 &&
          categoryEl.getBoundingClientRect().bottom < compactBar.offsetHeight,
      );
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);


  // 获取主题列表
  const fetchTopics = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await getActiveTopics();
      setTopics(result);
    } catch (error) {
      showUserError('主题加载失败，请稍后刷新重试');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTopics();
  }, [fetchTopics]);

  useEffect(() => {
    if (PUBLIC_CATALOG_MODE) return;

    const fetchContactConfig = async () => {
      try {
        const result = await getPublicContactConfig();
        setContactConfig(result);
      } catch {
        setContactConfig(DEFAULT_CONTACT_CONFIG);
      }
    };
    fetchContactConfig();
  }, []);

  useEffect(() => { startAnalytics(); }, []);
  const handleContactAction = (position: 'header' | 'detail' | 'passcode') => {
    if (!contactConfig.enabled) return;
    trackAnalytics({kind:'contact',position});

    if (contactConfig.method === 'link') {
      if (!contactConfig.link_url) {
        showUserError('客服联系方式暂未设置，请稍后再试');
        return;
      }
      window.open(contactConfig.link_url, '_blank', 'noopener,noreferrer');
      return;
    }

    const hasWechatId = !!contactConfig.wechat_id?.trim();
    const hasQrImage = !!contactConfig.qr_image_url?.trim();

    if (!hasWechatId && !hasQrImage) {
      showUserError('客服联系方式暂未设置，请稍后再试');
      return;
    }
    setIsContactDialogOpen(true);
  };

  const handleCopyWechatId = async () => {
    const wechatId = contactConfig.wechat_id?.trim();
    if (!wechatId) {
      showUserError('客服微信号暂未设置，请稍后再试');
      return;
    }

    try {
      await navigator.clipboard.writeText(wechatId);
      showUserSuccess('微信号已复制');
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = wechatId;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      showUserSuccess('微信号已复制');
    }
  };

  const handleHiddenAdminEntry = () => {
    const nextCount = adminTapCount + 1;
    setAdminTapCount(nextCount);

    if (nextCount >= 5) {
      setAdminTapCount(0);
      navigate(ADMIN_LOGIN_PATH);
      return;
    }

    window.setTimeout(() => {
      setAdminTapCount((current) => (current === nextCount ? 0 : current));
    }, 2500);
  };

  const isTotalMode = session?.member.quota_mode === "total";
const cycleRemainLabel = isTotalMode ? "总次数剩余" : "本周期剩余";
  const cycleDates = !isTotalMode && session?.member.cycle_start_date
    ? getCycleDateRange(session.member.cycle_start_date)
    : null;
  // 查看详情
  const handleViewDetail = (topic: ResourceTopic) => {
    trackLegacyView(() => trackTopicView(topic.topic_name));
    trackAnalytics({kind:'detail',topicId:topic.id});
    setDetailTopic(topic);
    setDetailPreviewKind(topic.video_display ? 'video' : topic.ppt_display ? 'ppt' : 'script');
    setIsDetailOpen(true);
  };

  // 打开领取流程
  const handleOpenRedeem = (topic: ResourceTopic) => {
    if (session) {
      // 已登录，检查额度（领取动作的额度拦截保持不变）
      if (session.remainingCycle <= 0) {
        showQuotaExhaustedToast(getQuotaExhaustedMessage(session.member));
        return;
      }
      if (session.remainingToday <= 0) {
        showUserInfo(MEMBER_ERROR_MESSAGES.DAILY_LIMIT_REACHED);
        return;
      }
      setConfirmTopic(topic);
    } else {
      // 未登录，先要求输入兑换码
      setPendingRedeem({ topic });
      setPasscodeDialogMode('redeem');
      setPasscode('');
      setPasscodeError('');
      setIsPasscodeDialogOpen(true);
    }
  };

  const handleOpenLogin = () => {
    setPendingRedeem(null);
    setPasscodeDialogMode('login');
    setPasscode('');
    setPasscodeError('');
    setIsPasscodeDialogOpen(true);
  };

  const handleDurationFilterClick = (
    value: DurationRange | typeof DURATION_ALL_VALUE,
  ) => {
    if (value === DURATION_ALL_VALUE) {
      setDurationRanges([]);
      return;
    }

    setDurationRanges((current) =>
      current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value],
    );
  };

  const handleClearFilters = () => {
    setDurationRanges([]);
    setCategory('');
    setUnclaimedOnly(false);
  };

  // 验证兑换码
  const handleVerifyPasscode = async () => {
    const normalizedPasscode = normalizePasscode(passcode);

    if (!normalizedPasscode) {
      setPasscodeError('请输入兑换码');
      return;
    }
    if (!isValidPasscode(normalizedPasscode)) {
      setPasscodeError('兑换码必须为 6–32 位数字或英文字母；最外侧空白会自动去除，内部空格、中文和特殊符号不支持');
      return;
    }

    setIsVerifying(true);
    try {
      const result = await verifyPasscode({
        passcode: normalizedPasscode,
        topic_name: pendingRedeem?.topic.topic_name,
      });

      if (!result.success) {
        const message = getMemberErrorMessage(result.error_code);
        setPasscodeError(message);
        return;
      }

      if (result.member) {
        // 保存会话（额度用完也正常登录，不拦截登录流程）
        login({
          passcode: normalizedPasscode,
          member: result.member,
          remainingCycle: result.remaining_cycle,
          remainingToday: result.remaining_today,
        });
        setPasscodeError('');

        // 批量码第一次使用：先在弹窗里选填订单号，再继续
        if (result.just_activated && result.member.batch_id != null) {
          setActivationStep(result);
          return;
        }
        continueAfterLogin(result);
      }
    } catch (error) {
      const message = extractMemberErrorMessage(error);
      setPasscodeError(message);
    } finally {
      setIsVerifying(false);
    }
  };

  const continueAfterLogin = (result: MemberVerifyResult) => {
    // 登录成功即关闭兑换码弹窗，额度提示用轻提示展示，不再显示“登录失败”式红色错误
    setIsPasscodeDialogOpen(false);
    setActivationStep(null);
    if (!result.member) return;

    // 周期/总额度用完：正常登录，按额度类型提示恢复时间
    if (result.remaining_cycle <= 0) {
      showQuotaExhaustedToast(getQuotaExhaustedMessage(result.member));
      return;
    }
    // 仅今日额度用完：正常登录，提示明日恢复
    if (result.remaining_today <= 0) {
      showUserInfo(MEMBER_ERROR_MESSAGES.DAILY_LIMIT_REACHED);
      return;
    }

    // 额度正常，继续领取流程
    if (pendingRedeem) {
      setConfirmTopic(pendingRedeem.topic);
      setPendingRedeem(null);
    }
  };

  // 确认领取
  const confirmRedeem = async () => {
    if (!session || !confirmTopic) return;

    setIsRedeeming(true);
    try {
      const result = await redeemResource({
        passcode: session.passcode,
        topic_name: confirmTopic.topic_name,

      });

      if (result.already_redeemed) {
        setConfirmTopic(null);
        showUserToast(getMemberErrorMessage('ALREADY_REDEEMED'));
        return;
      }

      // 更新额度
      updateQuota(result.remaining_cycle, result.remaining_today);
      try {
        const latestMember = await getMemberByPasscode(session.passcode);
        if (latestMember) {
          refreshSession({
            member: latestMember,
            remainingCycle: result.remaining_cycle,
            remainingToday: result.remaining_today,
          });
        }
      } catch {
        // 领取成功时，不让会员信息刷新失败打断主流程
      }

      // 关闭弹窗
      setConfirmTopic(null);

      // 跳转到成功页
      navigate('/success', {
        state: {
          topicName: result.topic_name,
          memberType: result.member_type,
          downloadLink: result.download_link,
          remainingCycle: result.remaining_cycle,
          remainingToday: result.remaining_today,
        },
      });
    } catch (error: any) {
      showUserError(extractMemberErrorMessage(error));
    } finally {
      setIsRedeeming(false);
    }
  };



  // 筛选主题
  const normalizedSearch = search.trim().toLowerCase();

  const getSearchRank = (topic: ResourceTopic) => {
    if (!normalizedSearch) return 0;

    const topicName = (topic.topic_name || '').toLowerCase();
    if (topicName.includes(normalizedSearch)) return 2;
    return 0;
  };

  const isClaimed = (topic: ResourceTopic) => isTopicClaimed(claimedTopics, topic);
  const effectiveUnclaimedOnly = !!session && unclaimedOnly;

  // 除分类以外的条件（搜索、时长、只看未领取），分类按钮上的数量也按这些条件计算
  const matchesNonCategoryFilters = (
    topic: ResourceTopic,
    ranges: DurationRange[] = durationRanges,
    unclaimed: boolean = effectiveUnclaimedOnly,
  ) => {
    const matchSearch = !normalizedSearch || getSearchRank(topic) > 0;
    const matchDuration = isDurationInRanges(topic.estimated_duration, ranges);
    const matchUnclaimed = !unclaimed || !isClaimed(topic);
    return matchSearch && matchDuration && matchUnclaimed;
  };

  const nonCategoryMatches = topics.filter((topic) => matchesNonCategoryFilters(topic));
  const categoryCounts: Partial<Record<ContentCategory | '', number>> = { '': nonCategoryMatches.length };
  for (const topic of nonCategoryMatches) {
    categoryCounts[topic.content_category] = (categoryCounts[topic.content_category] ?? 0) + 1;
  }

  const filteredTopics = nonCategoryMatches
    .filter((topic) => !category || topic.content_category === category)
    .sort((a, b) => getSearchRank(b) - getSearchRank(a));

  const claimedCount = session ? topics.filter(isClaimed).length : 0;

  const selectedDurationFilters = durationRanges.map((range) => ({
    value: range,
    label: getDurationRangeLabel(range),
  }));
  const selectedCategoryLabel = category ? CONTENT_CATEGORY_LABELS[category] : '';
  const hasActiveFilters = durationRanges.length > 0 || !!category || effectiveUnclaimedOnly;
  const selectedFilterSummary = hasActiveFilters
    ? [
        ...selectedDurationFilters.map((filter) => filter.label),
        selectedCategoryLabel,
        effectiveUnclaimedOnly ? '只看未领取' : '',
      ]
        .filter(Boolean)
        .join(' / ')
    : '全部主题';
  const durationButtonLabel =
    durationRanges.length === 0
      ? '时长不限'
      : durationRanges.length === 1
        ? selectedDurationFilters[0].label
        : `时长 · ${durationRanges.length} 项`;
  // 筛不到结果时，只放宽卡住的那一项：先放时长，再放题材，最后显示已领取的
  const countWith = (next: { ranges?: DurationRange[]; category?: ContentCategory | ''; unclaimed?: boolean }) => {
    const nextCategory = next.category ?? category;
    return topics.filter(
      (topic) =>
        matchesNonCategoryFilters(topic, next.ranges ?? durationRanges, next.unclaimed ?? effectiveUnclaimedOnly) &&
        (!nextCategory || topic.content_category === nextCategory),
    ).length;
  };
  // 依次试“放宽时长 → 放宽题材 → 显示已领取的”，给第一个放宽后有结果的；题材本身就没结果时也能继续往下找
  const relaxSuggestion = (() => {
    const scope = selectedCategoryLabel || '全部题材';
    const candidates = [
      durationRanges.length > 0 && {
        tip: `「${scope}」里暂时没有「${selectedDurationFilters.map((filter) => filter.label).join('、')}」的稿子。`,
        label: '时长改成「不限」',
        count: countWith({ ranges: [] }),
        apply: () => setDurationRanges([]),
      },
      !!category && {
        tip: `「${selectedCategoryLabel}」里暂时没有符合条件的稿子。`,
        label: '题材改成「全部」',
        count: countWith({ category: '' }),
        apply: () => setCategory(''),
      },
      effectiveUnclaimedOnly && {
        tip: '符合条件的主题你都领过了。',
        label: '显示已领取的',
        count: countWith({ unclaimed: false }),
        apply: () => setUnclaimedOnly(false),
      },
    ].filter((item): item is { tip: string; label: string; count: number; apply: () => void } => !!item);
    return candidates.find((item) => item.count > 0) ?? candidates[0] ?? null;
  })();

  // 手机宽度下用封面三列 / 列表；平板和桌面保持卡片网格
  const isPhoneLayout = isMobileWidth;
  const isQuickList = isPhoneLayout && mobileViewMode === 'list';
  const isCoverGrid = isPhoneLayout && mobileViewMode === 'cover';
  // “筛选”按钮上的数字只算藏在筛选面板里的条件（时长、只看未领取）；分类在页面上直接看得到，不重复计数
  const activeFilterCount = durationRanges.length + (effectiveUnclaimedOnly ? 1 : 0);

  // 已经翻过结果开头时，跳回结果开头，从第一个结果看起
  const scrollToResultsStart = () => {
    const anchor = resultsAnchorRef.current;
    if (!anchor) return;
    const headerHeight = getStickyHeaderHeight();
    const anchorTop = anchor.getBoundingClientRect().top;
    if (anchorTop >= headerHeight) return;
    window.scrollTo({ top: window.scrollY + anchorTop - headerHeight - 8 });
  };
  // 在列表中间换了分类/筛选条件：跳回结果开头
  const isFirstFilterRenderRef = useRef(true);
  useEffect(() => {
    if (isFirstFilterRenderRef.current) {
      isFirstFilterRenderRef.current = false;
      return;
    }
    scrollToResultsStart();
  }, [category, durationRanges, effectiveUnclaimedOnly]);

  const openTopicResource = (topic: ResourceTopic) => {
    setHistoryQuery(topic.topic_name);
    setIsHistoryOpen(true);
  };

  const getPreviewGridClassName = (count: number) => {
    if (count <= 1) return 'grid grid-cols-1 gap-3';
    return 'grid grid-cols-2 gap-3';
  };

  const renderCover = (topic: ResourceTopic, variant: 'card' | 'tile' | 'thumb') => {
    if (topic.cover_image) {
      return (
        <Image
          src={topic.cover_image}
          alt={topic.topic_name}
          className="pointer-events-none h-full w-full select-none object-cover transition-transform duration-500 group-hover:scale-105"
          width={variant === 'card' ? 300 : 160}
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          onDragStart={(e) => e.preventDefault()}
          style={PROTECTED_PREVIEW_STYLE}
        />
      );
    }
    // 没有封面时用分类色块占位，主题名直接写在上面
    const style = getCategoryStyle(topic.content_category);
    return (
      <div className="relative h-full w-full overflow-hidden" style={{ background: style.coverBg }}>
        <span
          aria-hidden
          className="absolute -right-6 -top-8 h-24 w-24 rounded-full opacity-15"
          style={{ background: style.coverInk }}
        />
        {variant !== 'thumb' && (
          <span
            className={`relative block font-bold leading-tight ${
              variant === 'tile' ? 'p-1.5 text-[11px]' : 'p-3 text-base sm:p-4 sm:text-xl'
            }`}
            style={{ color: style.coverInk }}
          >
            {topic.topic_name}
          </span>
        )}
      </div>
    );
  };

  const mobileChipClass = (active: boolean) =>
    `flex h-9 items-center justify-center whitespace-nowrap rounded-full border text-[13px] font-medium transition-colors ${
      active
        ? 'border-cta bg-cta text-cta-foreground'
        : 'border-border bg-white text-foreground'
    }`;
  // 手机收起后的条件栏按钮
  const compactChipClass = (active: boolean) =>
    `flex h-[42px] min-w-0 flex-1 basis-0 items-center justify-center gap-0.5 whitespace-nowrap rounded-full px-2 text-sm font-bold transition-colors ${
      active ? 'bg-cta text-cta-foreground' : 'bg-[#F1E8DB] text-[#2E241B]'
    }`;
  // 桌面左侧筛选栏选项（柔色块，无描边；选中为白底橙色描边）；dim：当前条件下这一项没有主题
  const sideOptionClass = (active: boolean, dim = false) =>
    `flex h-10 min-w-0 items-center gap-2 whitespace-nowrap rounded-xl px-2.5 text-sm transition-colors ${
      active
        ? 'bg-white font-semibold text-orange-800 shadow-[inset_0_0_0_1.5px_var(--cta)]'
        : 'bg-[#F7F2EA] text-[#3A2F25] hover:bg-[#F1EADF]'
    } ${dim && !active ? 'opacity-45' : ''}`;
  const pickedChipClass =
    'h-7 rounded-full border border-orange-100 bg-white px-3 text-xs text-foreground hover:text-orange-800';
  const resultTitle = normalizedSearch
    ? `搜「${search.trim()}」找到 ${filteredTopics.length} 个`
    : hasActiveFilters
      ? `找到 ${filteredTopics.length} 个主题`
      : `全部主题 · 共 ${filteredTopics.length} 个`;

  // 详情内容：手机抽屉和桌面弹窗共用，标题组件按外壳传入（两者的无障碍标题不能混用）
  // wide：桌面宽屏双栏（左预览、右核心价值观与包含资源，资源全部展开）
  const renderDetailBody = (
    TitleComp: typeof DialogTitle | typeof DrawerTitle,
    DescComp: typeof DialogDescription | typeof DrawerDescription,
    wide = false,
  ) => {
    if (!detailTopic) return null;
    const previews = getDetailPreviews(detailTopic);
    const activeIndex = Math.max(0, previews.findIndex((item) => item.kind === detailPreviewKind));

    const gallery = (
      <div
        className={
          wide
            ? 'space-y-2'
            : 'space-y-2 px-4 pt-1 max-md:flex max-md:min-h-[180px] max-md:shrink max-md:flex-col md:pt-1'
        }
      >
        {previews.length === 0 ? (
          <div className="relative aspect-video w-full overflow-hidden rounded-2xl bg-muted">
            {renderCover(detailTopic, 'card')}
          </div>
        ) : (
          /* 大图可左右滑动切换（原生滚动吸附），与下方缩略图同步 */
          <div
            ref={galleryRef}
            onScroll={handleGalleryScroll}
            className="flex snap-x snap-mandatory overflow-x-auto overscroll-x-contain rounded-2xl max-md:min-h-[120px] max-md:shrink max-md:grow-0 max-md:basis-[calc((100vw-2rem)*0.5625)] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            {previews.map((item) => (
              <div key={item.kind} className="w-full shrink-0 snap-center snap-always">
                {item.kind === 'video' && detailTopic.video_display ? (
                  <>
                    {/* 视频预览缩略图 - 点击前仅显示封面，避免请求视频流量 */}
                    <button
                      type="button"
                      aria-label="播放示范视频"
                      className="group relative block aspect-video w-full overflow-hidden rounded-2xl bg-muted max-md:aspect-auto max-md:h-full"
                      onClick={() => setPreviewVideo(detailTopic.video_display)}
                    >
                      <video
                        src={detailTopic.video_display}
                        poster={detailTopic.cover_image || undefined}
                        className="pointer-events-none h-full w-full object-cover"
                        preload="none"
                      />
                      <span className="absolute left-1/2 top-1/2 flex h-14 w-14 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white/95 shadow-lg transition-transform group-hover:scale-105">
                        <Play className="ml-0.5 h-6 w-6 fill-cta text-cta" />
                      </span>
                      {/* 类型标签 */}
                      <span className="absolute bottom-2.5 left-2.5 inline-flex h-6 items-center rounded-full bg-black/65 px-2.5 text-xs text-white">
                        示范视频 · 点击播放
                      </span>
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    aria-label={`放大查看${item.label}`}
                    onClick={() => setPreviewImage(item.src)}
                    className="group relative block aspect-video w-full overflow-hidden rounded-2xl bg-muted max-md:aspect-auto max-md:h-full"
                  >
                    <img
                      src={item.src || undefined}
                      alt={`${item.label}预览`}
                      className="pointer-events-none h-full w-full select-none object-cover object-top"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      onDragStart={(e) => e.preventDefault()}
                      style={PROTECTED_PREVIEW_STYLE}
                    />
                    <PreviewWatermark size="lg" />
                    <span className="absolute bottom-2.5 left-2.5 inline-flex h-6 items-center gap-1 rounded-full bg-black/65 px-2.5 text-xs text-white">
                      <Maximize2 className="h-3 w-3" />
                      {item.label}预览 · 点击放大
                    </span>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {previews.length > 1 && (
          <div className={`grid shrink-0 gap-2 ${previews.length === 3 ? 'grid-cols-3' : 'grid-cols-2'}`} role="tablist" aria-label="切换预览">
            {previews.map((item, index) => {
              const selected = index === activeIndex;
              return (
                <button
                  key={item.kind}
                  type="button"
                  role="tab"
                  aria-selected={selected}
                  onClick={() => scrollGalleryTo(index, item.kind)}
                  className={`relative h-12 overflow-hidden rounded-lg bg-muted transition-shadow md:aspect-video md:h-auto ${
                    selected ? 'shadow-[0_0_0_2px_var(--cta)]' : 'opacity-80 shadow-[0_0_0_1px_#F0E4D4]'
                  }`}
                >
                  {item.src ? (
                    <img
                      src={item.src}
                      alt=""
                      className="pointer-events-none h-full w-full select-none object-cover object-top"
                      draggable={false}
                      onContextMenu={(e) => e.preventDefault()}
                      style={PROTECTED_PREVIEW_STYLE}
                    />
                  ) : (
                    renderCover(detailTopic, 'thumb')
                  )}
                  {item.kind !== 'video' && <PreviewWatermark />}
                  {item.kind === 'video' && (
                    <span className="absolute inset-0 flex items-center justify-center">
                      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/50">
                        <Play className="ml-px h-3 w-3 fill-white text-white" />
                      </span>
                    </span>
                  )}
                  <span className="absolute left-1 top-1 rounded bg-black/65 px-1.5 py-px text-[11px] font-medium text-white">
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    );

    const info = (
      <div className={wide ? 'space-y-4' : 'shrink-0 space-y-3 px-5 pb-4 pt-3 md:space-y-4 md:pt-4 md:pb-5'}>
        {detailTopic.core_value && (
          <section>
            <DetailSectionTitle icon={Sparkles}>核心价值观</DetailSectionTitle>
            <p className="rounded-xl bg-orange-50/70 px-3.5 py-2 text-[15px] leading-relaxed text-foreground md:py-2.5">
              {detailTopic.core_value}
            </p>
          </section>
        )}

        <section>
          <DetailSectionTitle icon={FileText}>包含资源</DetailSectionTitle>
          <div className={wide ? 'space-y-1.5' : 'space-y-2'}>
            {PRIMARY_RESOURCES.map((item) => {
              const Icon = item.icon;
              return (
                <div
                  key={item.title}
                  className={`flex items-center gap-3 rounded-xl bg-white px-3 shadow-[0_0_0_1px_#F0E4D4] ${wide ? 'py-1.5' : 'py-2 md:py-2.5'}`}
                >
                  <span
                    className={`flex shrink-0 items-center justify-center rounded-xl bg-orange-50 text-cta ${wide ? 'h-9 w-9' : 'h-9 w-9 md:h-10 md:w-10'}`}
                  >
                    <Icon className="h-5 w-5" />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[15px] font-semibold text-foreground">{item.title}</p>
                    <p className="flex gap-1 pt-1">
                      {item.points.map((point) => (
                        <span
                          key={point}
                          className="whitespace-nowrap rounded-md bg-orange-50 px-1.5 py-0.5 text-xs font-medium text-orange-900"
                        >
                          {point}
                        </span>
                      ))}
                    </p>
                  </div>
                </div>
              );
            })}
            {wide ? (
              /* 桌面地方够：其余资源也各占一行，不再用小字带过 */
              SECONDARY_RESOURCES.map((item) => {
                const Icon = item.icon;
                return (
                  <div
                    key={item.title}
                    className="flex min-h-11 items-center gap-3 rounded-xl bg-white px-3 py-1 shadow-[0_0_0_1px_#F0E4D4]"
                  >
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-orange-50 text-cta">
                      <Icon className="h-5 w-5" />
                    </span>
                    <p className="text-[15px] font-semibold text-foreground">{item.title}</p>
                  </div>
                );
              })
            ) : (
              <p className="flex flex-wrap items-center gap-x-3 gap-y-1 px-1 pt-0.5 text-[13px] text-muted-foreground">
                <span>另含：</span>
                {SECONDARY_RESOURCES.map((item) => {
                  const Icon = item.icon;
                  return (
                    <span key={item.title} className="inline-flex items-center gap-1">
                      <Icon className="h-3.5 w-3.5 text-cta" />
                      {item.title}
                    </span>
                  );
                })}
              </p>
            )}
          </div>
        </section>
      </div>
    );

    const claimedDetail = !!session && isClaimed(detailTopic);
    const statusLine = PUBLIC_CATALOG_MODE ? null : claimedDetail ? (
      <p className="text-sm font-medium text-claimed">你已领取过这个主题，可随时查看资源，不再扣次数</p>
    ) : session && session.remainingCycle > 0 ? (
      <p className="text-sm text-foreground">
        领取将使用 <strong>1</strong> 次额度 · 领取后{cycleRemainLabel} <strong>{session.remainingCycle - 1}</strong> 次
      </p>
    ) : null;
    const contactHint = contactConfig.enabled ? contactConfig.detail_description : '支持兑换码领取、单独获取、续费咨询';
    const contactButton = contactConfig.enabled && (
      <Button
        type="button"
        variant="outline"
        onClick={() => handleContactAction('detail')}
        className={`rounded-xl border-orange-200 font-semibold text-orange-800 hover:bg-orange-50 ${wide ? 'h-12 px-7' : 'h-12'}`}
      >
        {contactConfig.button_text}
      </Button>
    );
    const mainButton = PUBLIC_CATALOG_MODE ? null : claimedDetail ? (
      <Button
        type="button"
        className={`h-12 gap-2 rounded-xl bg-claimed font-semibold text-white hover:bg-claimed/90 ${wide ? 'min-w-48 px-10' : ''}`}
        onClick={() => {
          setIsDetailOpen(false);
          openTopicResource(detailTopic);
        }}
      >
        <Check className="h-4 w-4" />
        查看资源
      </Button>
    ) : (
      <Button
        type="button"
        className={`h-12 gap-2 rounded-xl bg-cta font-semibold text-cta-foreground hover:bg-cta-hover ${wide ? 'min-w-48 px-10' : ''}`}
        onClick={() => {
          setIsDetailOpen(false);
          handleOpenRedeem(detailTopic);
        }}
      >
        <Gift className="h-4 w-4" />
        立即领取
      </Button>
    );

    return (
            <>
              {/* 结构：名称与时长（固定标题区）→ 大图预览（下方缩略图切换视频 / PPT / 文稿）→ 核心价值观 → 包含资源 */}
              {/* 标题区固定在顶部，像卡片的小标题；右侧留出关闭按钮的位置 */}
              <div className={`shrink-0 space-y-1 pb-2.5 pr-16 pt-3 md:pt-5 ${wide ? 'pl-7' : 'pl-5'}`}>
                <TitleComp className="text-xl font-bold leading-tight text-foreground">
                  {detailTopic.topic_name}
                </TitleComp>
                <DescComp className="text-sm text-muted-foreground">
                  约 <strong className="text-cta">{detailTopic.estimated_duration}</strong>
                  {' · '}
                  {CONTENT_CATEGORY_LABELS[detailTopic.content_category]}
                  {' · '}
                  {CONTENT_DIRECTION_LABELS[detailTopic.content_direction]}
                </DescComp>
              </div>
              {wide ? (
                <div className="grid min-h-0 flex-1 grid-cols-[minmax(0,540px)_minmax(0,1fr)] items-start gap-6 overflow-y-auto px-7 pb-5 pt-1">
                  {gallery}
                  {info}
                </div>
              ) : (
                /* 手机：屏幕偏矮时只压缩顶部大图（不低于 120px），文字和按钮尺寸不变，尽量一屏放下 */
                <div className="min-h-0 flex-1 overflow-y-auto max-md:flex max-md:flex-col">
                  {gallery}
                  {info}
                </div>
              )}

              {/* 底部操作区 */}
              {wide ? (
                <div className="flex shrink-0 items-center gap-4 border-t border-border/70 bg-white px-7 py-3.5">
                  <div className="min-w-0 flex-1 space-y-0.5">
                    {statusLine}
                    <p className="text-sm text-muted-foreground">{contactHint}</p>
                  </div>
                  {contactButton}
                  {mainButton}
                </div>
              ) : (
                <div className="shrink-0 border-t border-border/70 bg-white px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
                  {!PUBLIC_CATALOG_MODE && (
                    <div className="mb-2.5 space-y-0.5 md:mb-3">
                      {statusLine}
                      {/* 手机上省掉这行重复说明，保证详情一屏放下；“联系客服”按钮仍在 */}
                      <p className="hidden text-sm text-muted-foreground md:block">{contactHint}</p>
                    </div>
                  )}
                  <div className={`grid gap-3 ${contactConfig.enabled && !PUBLIC_CATALOG_MODE ? 'grid-cols-[1fr_2fr] sm:grid-cols-2' : 'grid-cols-1'}`}>
                    {contactButton}
                    {mainButton}
                  </div>
                </div>
              )}
            </>
    );
  };
  return (
    // overflow-x-clip 而不是 hidden：hidden 会让这一层变成滚动容器，导致下面的 sticky 顶栏失效
    <div className="min-h-screen overflow-x-clip bg-background">
      {/* 品牌栏：手机端随页面滚走，只把找内容的功能固定住；桌面端固定 */}
      <div
        ref={brandHeaderRef}
        className="relative z-30 bg-white md:sticky md:top-0 md:z-20 md:shadow-[0_1px_0_var(--border),0_4px_12px_rgba(80,50,20,0.05)]"
      >
        <div className="mx-auto max-w-7xl px-4 py-2.5 md:py-3">
          <div className="flex items-center justify-between gap-x-2 gap-y-2">
            {/* 品牌区 */}
            <div
              className="flex shrink-0 cursor-pointer items-center gap-2 select-none"
              onClick={handleHiddenAdminEntry}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleHiddenAdminEntry();
                }
              }}
            >
              <img
                src={brandLogoSrc}
                alt="少儿英语说图标"
                className="h-8 w-8 rounded-lg object-cover md:h-10 md:w-10"
              />
              <div className="leading-tight">
                <h1 className="text-[17px] font-bold md:text-lg">少儿英语说</h1>
                <p className="hidden text-xs text-muted-foreground md:block">英语演讲主题百宝箱</p>
              </div>
            </div>

            {/* 会员状态摘要区：仅 md+ 显示 */}
            {!PUBLIC_CATALOG_MODE && session && (
              <div className="hidden min-w-0 flex-1 justify-end md:flex">
                <div className="flex h-10 min-w-0 items-center gap-2.5 rounded-full border border-orange-100 bg-orange-50/60 pl-1.5 pr-3 text-[13px] text-muted-foreground">
                  <span className="inline-flex h-7 shrink-0 items-center rounded-full bg-orange-100 px-3 font-semibold text-orange-900">
                    {session.member.member_type}
                  </span>
                  <span className="whitespace-nowrap">
                    {cycleRemainLabel} <strong className="text-[15px] text-foreground">{session.remainingCycle}</strong>
                  </span>
                  {!isTotalMode && (
                    <>
                      <span aria-hidden className="h-3.5 w-px bg-orange-200" />
                      <span className="whitespace-nowrap">
                        今日剩余 <strong className="text-[15px] text-foreground">{session.remainingToday}</strong>
                      </span>
                    </>
                  )}
                  {!isTotalMode && cycleDates && (
                    <>
                      <span aria-hidden className="hidden h-3.5 w-px bg-orange-200 lg:block" />
                      <span className="hidden whitespace-nowrap lg:inline">{cycleDates.nextResetDate}重置</span>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* 已登录操作入口：桌面三个按钮；手机收进“我的会员”面板 */}
            {!PUBLIC_CATALOG_MODE && session && (
              <>
                <div className="hidden shrink-0 items-center gap-2 md:flex">
                  <MemberBenefitsDialog
                    session={session}
                    triggerClassName="h-9 rounded-xl border-orange-200 bg-white px-3 text-orange-900 hover:bg-orange-50"
                    onOpenHistory={() => {
                      setHistoryQuery('');
                      setIsHistoryOpen(true);
                    }}
                    onContact={contactConfig.enabled ? () => handleContactAction('header') : undefined}
                    onLogout={logout}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setHistoryQuery('');
                      setIsHistoryOpen(true);
                    }}
                    className="h-9 rounded-xl border-orange-200 bg-white px-3 text-orange-900 hover:bg-orange-50"
                  >
                    <History className="mr-1 h-4 w-4" />
                    领取记录
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={logout}
                    className="h-9 px-2.5 text-muted-foreground"
                  >
                    <LogOut className="mr-1 h-4 w-4" />
                    退出
                  </Button>
                </div>
                <button
                  type="button"
                  onClick={() => setIsMemberCenterOpen(true)}
                  className="flex h-10 shrink-0 items-center gap-2 rounded-full border border-orange-200 bg-orange-50/70 pl-3 pr-2.5 md:hidden"
                >
                  <span className="whitespace-nowrap text-xs text-muted-foreground">
                    剩 <strong className="text-[15px] text-cta">{session.remainingCycle}</strong> 次
                  </span>
                  <span aria-hidden className="h-3.5 w-px bg-orange-200" />
                  <span className="flex items-center whitespace-nowrap text-sm font-semibold text-orange-900">
                    我的权益
                    <ChevronRight className="h-4 w-4" />
                  </span>
                </button>
              </>
            )}

            {!PUBLIC_CATALOG_MODE && !session && (
              <div className="flex shrink-0 items-center gap-1 sm:gap-2">
                {contactConfig.enabled && contactConfig.show_header_entry && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleContactAction('header')}
                    className="h-9 gap-1 px-2 text-sm text-muted-foreground hover:text-foreground"
                  >
                    <MessageCircle className="h-4 w-4" />
                    {contactConfig.button_text || '联系客服'}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleOpenLogin}
                  className="h-9 rounded-full border-orange-200 px-4 text-sm font-semibold text-orange-800 hover:bg-orange-50"
                >
                  登录
                </Button>
              </div>
            )}
          </div>
        </div>

      </div>

      {/* 手机端搜索与分类：随页面滚走；分类滑出屏幕后，由顶部的条件栏接替 */}
      <div className="relative z-10 bg-white shadow-[0_1px_0_var(--border)] md:hidden">
        <div className="flex gap-2 px-4 pb-2.5 pt-0.5">
          <label className="flex h-11 min-w-0 flex-1 items-center gap-2 rounded-xl bg-[#F5F0E8] px-3.5 focus-within:ring-2 focus-within:ring-cta/25">
            <Search className="h-[18px] w-[18px] shrink-0 text-muted-foreground" aria-hidden />
            <span className="sr-only">搜索主题</span>
            <input
              ref={mobileSearchInputRef}
              type="search"
              enterKeyHint="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="搜主题名，如：花木兰"
              className="min-w-0 flex-1 bg-transparent text-base outline-none placeholder:text-muted-foreground [&::-webkit-search-cancel-button]:hidden"
            />
            {search && (
              <button
                type="button"
                aria-label="清空搜索"
                onClick={() => setSearch('')}
                className="-mr-2 flex h-9 w-9 items-center justify-center rounded-full text-muted-foreground"
              >
                <XCircle className="h-[18px] w-[18px]" />
              </button>
            )}
          </label>
          <button
            type="button"
            onClick={() => setIsMobileFilterOpen(true)}
            className="relative inline-flex h-11 shrink-0 items-center gap-1 rounded-xl border-[1.5px] border-cta bg-white px-3 text-[15px] font-bold text-orange-800"
          >
            <SlidersHorizontal className="h-4 w-4" />
            筛选
            {activeFilterCount > 0 && (
              <span className="absolute -right-2 -top-2 flex h-5 min-w-5 items-center justify-center rounded-full bg-cta px-1.5 text-xs font-bold text-white">
                {activeFilterCount}
              </span>
            )}
          </button>
        </div>

        {/* 手机端分类：“全部”竖着占两行，右边 8 个分类排成 4 × 2，上下左右都对齐 */}
        <div ref={mobileCategoryRef} className="grid grid-cols-5 grid-rows-2 gap-1.5 px-4 pb-2.5">
          <button
            type="button"
            onClick={() => setCategory('')}
            className={`row-span-2 !h-auto !rounded-2xl ${mobileChipClass(category === '')}`}
          >
            全部
          </button>
          {Object.entries(CONTENT_CATEGORY_LABELS).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setCategory(key as ContentCategory)}
              className={mobileChipClass(category === key)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>


      {/* 手机端收起后的条件栏：分类区滑出屏幕才出现，点任意一项弹出“改一下条件” */}
      <div
        ref={compactBarRef}
        aria-hidden={!showCompactBar}
        className={`fixed inset-x-0 top-0 z-30 bg-white px-3 pb-2 pt-[max(0.625rem,env(safe-area-inset-top))] shadow-[0_2px_0_#E2D3BF,0_8px_20px_rgba(60,32,10,0.16)] transition-[transform,opacity] duration-200 ease-out md:hidden ${
          showCompactBar ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0'
        }`}
      >
        <div className="flex gap-1.5">
          <button
            type="button"
            tabIndex={showCompactBar ? 0 : -1}
            onClick={() => setIsMobileFilterOpen(true)}
            className={compactChipClass(!!category)}
          >
            <span className="truncate">{selectedCategoryLabel || '全部题材'}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          </button>
          <button
            type="button"
            tabIndex={showCompactBar ? 0 : -1}
            onClick={() => setIsMobileFilterOpen(true)}
            className={compactChipClass(durationRanges.length > 0)}
          >
            <span className="truncate">{durationButtonLabel}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          </button>
          <button
            type="button"
            tabIndex={showCompactBar ? 0 : -1}
            aria-label="更多筛选"
            onClick={() => setIsMobileFilterOpen(true)}
            className={`relative !flex-none !basis-[42px] !px-0 ${compactChipClass(effectiveUnclaimedOnly)}`}
          >
            <SlidersHorizontal className="h-4 w-4" />
          </button>
          <button
            type="button"
            tabIndex={showCompactBar ? 0 : -1}
            aria-label="搜索主题"
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              mobileSearchInputRef.current?.focus({ preventScroll: true });
            }}
            className={`!flex-none !basis-[42px] !px-0 ${compactChipClass(false)}`}
          >
            <Search className="h-4 w-4" />
          </button>
        </div>
        <p className="truncate pl-1 pt-1.5 text-xs text-[#5C5045]">
          共 <strong className="text-cta">{filteredTopics.length}</strong> 个
          {hasActiveFilters ? ` · ${selectedFilterSummary}` : ''} · 点上面改条件
        </p>
      </div>

      {/* 桌面端标题与搜索 */}
      <section className="relative hidden overflow-hidden md:block">
        <div className="relative mx-auto max-w-7xl px-4 pb-6 pt-10">
          <h2 className="text-[28px] font-bold leading-tight text-foreground">给孩子挑一篇合适的英语演讲</h2>
          <p className="mt-2 text-base text-muted-foreground">
            每个主题都配齐示范视频、背景 PPT、中英双语文稿、示范音频与背景音乐
          </p>
          <form
            className="mt-5 flex max-w-3xl gap-2 rounded-2xl bg-white p-2 shadow-[0_8px_30px_rgba(120,72,28,0.10),0_0_0_1px_#EFE4D5]"
            onSubmit={(e) => e.preventDefault()}
          >
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="搜索主题名称，例如：花木兰、守株待兔、孔融让梨"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-12 border-0 pl-12 text-base shadow-none focus-visible:ring-0"
              />
            </div>
            <Button
              type="submit"
              className="h-12 rounded-xl bg-cta px-8 text-base font-semibold text-cta-foreground hover:bg-cta-hover"
            >
              搜索
            </Button>
          </form>
        </div>
      </section>

      {/* 主内容区：桌面端左侧固定筛选栏 + 右侧结果；手机端只有结果 */}
      <div className="mx-auto max-w-7xl px-4 pb-10 pt-3 md:grid md:grid-cols-[264px_minmax(0,1fr)] md:items-start md:gap-6 md:pt-0">
        {/* 桌面筛选栏：随页面固定在品牌栏下方，往下翻也一直看得见 */}
        <aside
          aria-label="筛选主题"
          className="sticky top-[84px] hidden max-h-[calc(100dvh-100px)] flex-col gap-5 overflow-y-auto rounded-2xl bg-white p-[18px] shadow-[0_0_0_1px_#F0E9DF] md:flex"
        >
          <section>
            <h2 className="text-[15px] font-bold leading-tight text-foreground">{TOPIC_FILTER_LABELS.category}</h2>
            <p className="mb-2.5 mt-0.5 text-xs text-muted-foreground">{TOPIC_FILTER_LABELS.categoryHint}</p>
            <div className="grid grid-cols-2 gap-1.5">
              <button
                type="button"
                aria-pressed={category === ''}
                onClick={() => setCategory('')}
                className={`col-span-2 ${sideOptionClass(category === '')}`}
              >
                全部题材
              </button>
              {Object.entries(CONTENT_CATEGORY_LABELS).map(([key, label]) => {
                const count = categoryCounts[key as ContentCategory] ?? 0;
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={category === key}
                    onClick={() => setCategory(key as ContentCategory)}
                    className={sideOptionClass(category === key, count === 0)}
                  >
                    <CategoryFilterIcon category={key} />
                    {label}
                  </button>
                );
              })}
            </div>
          </section>

          <section>
            <h2 className="text-[15px] font-bold leading-tight text-foreground">{TOPIC_FILTER_LABELS.duration}</h2>
            <p className="mb-2.5 mt-0.5 text-xs text-muted-foreground">{TOPIC_FILTER_LABELS.durationHint}</p>
            <div className="grid grid-cols-2 gap-1.5">
              {DURATION_FILTER_OPTIONS.map((option) => {
                const isActive =
                  option.value === DURATION_ALL_VALUE
                    ? durationRanges.length === 0
                    : durationRanges.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    aria-pressed={isActive}
                    onClick={() => handleDurationFilterClick(option.value)}
                    className={`${option.value === DURATION_ALL_VALUE ? 'col-span-2 justify-center' : ''} ${sideOptionClass(isActive)}`}
                  >
                    {option.value === DURATION_ALL_VALUE ? (
                      '时长不限'
                    ) : (
                      <>
                        <DurationDialIcon range={option.value} />
                        {option.label}
                      </>
                    )}
                  </button>
                );
              })}
            </div>
          </section>

          {session && (
            <label className="flex cursor-pointer items-center justify-between gap-3 border-t border-[#F3ECE2] pt-3.5 text-sm text-foreground">
              只看未领取的
              <Switch
                checked={unclaimedOnly}
                onCheckedChange={setUnclaimedOnly}
                aria-label="只看未领取"
                className="data-[state=checked]:bg-cta"
              />
            </label>
          )}

          <button
            type="button"
            onClick={handleClearFilters}
            disabled={!hasActiveFilters}
            className="h-9 shrink-0 self-center px-3 text-sm text-muted-foreground underline decoration-[#D8CCBB] underline-offset-[3px] transition-colors hover:text-orange-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            全部重选
          </button>
        </aside>

        <div className="min-w-0">
        {/* 结果栏 */}
        <div ref={resultsAnchorRef} className="mb-3">
          {/* 手机：数量 + 清空 + 封面 / 列表切换 */}
          <div className="flex min-h-9 items-center justify-between gap-2 md:hidden">
            <p className="min-w-0 truncate text-[13px] text-muted-foreground">
              共 <strong className="text-foreground">{filteredTopics.length}</strong> 个
              {session && claimedCount > 0 && (
                <>
                  {' · 已领 '}
                  <strong className="text-claimed">{claimedCount}</strong> 个
                </>
              )}
            </p>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="h-9 shrink-0 px-1 text-[13px] font-medium text-orange-800"
              >
                清空筛选
              </button>
            )}
            <div
              role="group"
              aria-label="浏览方式"
              className="ml-auto inline-flex shrink-0 rounded-[10px] bg-[#EFE8DD] p-0.5"
            >
              <button
                type="button"
                aria-pressed={mobileViewMode === 'cover'}
                onClick={() => setMobileViewMode('cover')}
                className={`inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-[13px] ${
                  mobileViewMode === 'cover' ? 'bg-white font-semibold text-foreground shadow-sm' : 'font-medium text-muted-foreground'
                }`}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
                封面
              </button>
              <button
                type="button"
                aria-pressed={mobileViewMode === 'list'}
                onClick={() => setMobileViewMode('list')}
                className={`inline-flex h-8 items-center gap-1 rounded-lg px-2.5 text-[13px] ${
                  mobileViewMode === 'list' ? 'bg-white font-semibold text-foreground shadow-sm' : 'font-medium text-muted-foreground'
                }`}
              >
                <List className="h-3.5 w-3.5" />
                列表
              </button>
            </div>
          </div>

          {/* 桌面：结果标题 + 已选条件（点 × 单独去掉） */}
          <div className="hidden min-h-10 items-center justify-between gap-3 md:flex">
            <p className="min-w-0 shrink-0 text-lg font-bold text-foreground">
              {resultTitle}
              {session && claimedCount > 0 && (
                <span className="ml-2 text-[13px] font-normal text-muted-foreground">
                  已领取 <strong className="text-claimed">{claimedCount}</strong> 个
                </span>
              )}
            </p>
            {hasActiveFilters && (
              <div className="flex min-w-0 flex-wrap justify-end gap-1.5">
                {category && selectedCategoryLabel && (
                  <button type="button" onClick={() => setCategory('')} className={pickedChipClass}>
                    {selectedCategoryLabel} ×
                  </button>
                )}
                {selectedDurationFilters.map((filter) => (
                  <button
                    key={filter.value}
                    type="button"
                    onClick={() =>
                      setDurationRanges((current) => current.filter((item) => item !== filter.value))
                    }
                    className={pickedChipClass}
                  >
                    {filter.label} ×
                  </button>
                ))}
                {effectiveUnclaimedOnly && (
                  <button type="button" onClick={() => setUnclaimedOnly(false)} className={pickedChipClass}>
                    只看未领取 ×
                  </button>
                )}
              </div>
            )}
          </div>
        </div>

        {/* 主题列表 */}
        {isLoading ? (
          /* 骨架屏：按最终布局占位，加载完成时页面不跳动 */
          <div
            aria-busy="true"
            aria-label="主题加载中"
            className={`grid gap-2 sm:gap-4 ${isCoverGrid ? 'grid-cols-3' : 'grid-cols-2 lg:grid-cols-3 xl:grid-cols-4'}`}
          >
            {Array.from({ length: isCoverGrid ? 12 : 8 }, (_, index) => (
              <div key={index} className="overflow-hidden rounded-xl bg-white shadow-[0_0_0_1px_#F0E9DF]">
                <div className="aspect-video animate-pulse bg-muted" />
                <div className="space-y-1.5 p-2">
                  <div className="h-3 w-3/4 animate-pulse rounded bg-muted" />
                  <div className="h-2.5 w-1/2 animate-pulse rounded bg-muted" />
                </div>
              </div>
            ))}
          </div>
        ) : filteredTopics.length === 0 ? (
          /* 筛不到时：说清是哪个条件卡住了，并给一个只放宽这一项的按钮 */
          <div className="mx-auto max-w-md py-12 text-center md:max-w-none md:rounded-2xl md:bg-white md:px-6 md:py-11 md:shadow-[0_0_0_1px_#F0E9DF]">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-muted">
              <SearchX className="h-8 w-8 text-muted-foreground/70" />
            </div>
            <h3 className="text-lg font-semibold">
              {normalizedSearch ? `没有找到「${search.trim()}」` : '没有完全符合的主题'}
            </h3>
            <p className="mt-1 text-sm text-muted-foreground">
              {normalizedSearch
                ? '试试更短的关键词，或者按题材找找看'
                : relaxSuggestion?.tip ?? '换个条件试试，或清空筛选看全部主题'}
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-2">
              {relaxSuggestion && relaxSuggestion.count > 0 && (
                <Button
                  className="h-11 rounded-xl bg-cta px-5 text-[15px] font-semibold text-cta-foreground hover:bg-cta-hover"
                  onClick={relaxSuggestion.apply}
                >
                  {relaxSuggestion.label}，有 {relaxSuggestion.count} 个
                </Button>
              )}
              {normalizedSearch && (
                <Button variant="outline" className="h-11 rounded-xl" onClick={() => setSearch('')}>
                  清空搜索
                </Button>
              )}
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={handleClearFilters}
                className="mt-3 h-9 px-2 text-[13px] font-semibold text-orange-800"
              >
                全部清空
              </button>
            )}
          </div>
        ) : isQuickList ? (
          <div className="overflow-hidden rounded-2xl bg-white shadow-[0_0_0_1px_#F0E9DF]" data-ai-section-type="card-list">
            {filteredTopics.map((topic) => {
              const claimed = isClaimed(topic);
              const style = getCategoryStyle(topic.content_category);
              return (
                <div
                  key={topic.id}
                  className="flex items-center gap-3 border-b border-[#F3ECE2] px-3 py-2.5 last:border-b-0"
                >
                  <button
                    type="button"
                    onClick={() => handleViewDetail(topic)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <div className="relative h-10 w-16 shrink-0 overflow-hidden rounded-lg bg-muted">
                      {renderCover(topic, 'thumb')}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[15px] font-semibold text-foreground">{topic.topic_name}</p>
                      <p className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                        <span aria-hidden className="h-1.5 w-1.5 rounded-full" style={{ background: style.dot }} />
                        {CONTENT_CATEGORY_LABELS[topic.content_category]} · {topic.estimated_duration}
                      </p>
                    </div>
                  </button>
                  {!PUBLIC_CATALOG_MODE &&
                    (claimed ? (
                      <button
                        type="button"
                        onClick={() => openTopicResource(topic)}
                        className="h-9 shrink-0 rounded-full bg-claimed-soft px-3 text-[13px] font-semibold text-claimed"
                      >
                        已领取
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => handleOpenRedeem(topic)}
                        className="h-9 shrink-0 rounded-full bg-orange-50 px-3.5 text-[13px] font-semibold text-cta"
                      >
                        领取
                      </button>
                    ))}
                </div>
              );
            })}
          </div>
        ) : isCoverGrid ? (
          /* 手机默认：封面三列（参照线上“速览”样式）：白底小卡片，封面保持 16:9 原比例，下方名称 + 时长 */
          <div className="grid grid-cols-3 gap-2" data-ai-section-type="card-list">
            {filteredTopics.map((topic) => {
              const claimed = isClaimed(topic);
              return (
                <button
                  key={topic.id}
                  type="button"
                  onClick={() => handleViewDetail(topic)}
                  className="group min-w-0 overflow-hidden rounded-xl bg-white text-left shadow-[0_1px_2px_rgba(80,50,20,0.06),0_0_0_1px_#F0E9DF] active:scale-[0.98]"
                >
                  <div className="relative aspect-video w-full overflow-hidden bg-muted">
                    {renderCover(topic, 'tile')}
                    {claimed && (
                      <span
                        aria-label="已领取"
                        className="absolute right-1 top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-claimed text-white shadow-sm"
                      >
                        <Check className="h-2.5 w-2.5" strokeWidth={4} />
                      </span>
                    )}
                  </div>
                  <div className="px-1.5 pb-1.5 pt-1">
                    <p className="truncate text-xs font-semibold leading-5 text-foreground">{topic.topic_name}</p>
                    <p className="flex items-center gap-0.5 text-[10px] font-medium text-orange-700">
                      <Clock className="h-2.5 w-2.5" />
                      {topic.estimated_duration}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div
            className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-3 xl:grid-cols-4"
            data-ai-section-type="card-list"
          >
            {filteredTopics.map((topic) => {
              const claimed = isClaimed(topic);
              const style = getCategoryStyle(topic.content_category);
              return (
                <Card
                  key={topic.id}
                  className="group cursor-pointer gap-0 overflow-hidden rounded-2xl border-0 bg-white py-0 shadow-[0_0_0_1px_#F0E9DF] transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-md"
                  role="button"
                  tabIndex={0}
                  onClick={() => handleViewDetail(topic)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleViewDetail(topic);
                    }
                  }}
                >
                  {/* 封面图 */}
                  <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted sm:aspect-video">
                    {renderCover(topic, 'card')}
                    <span className="absolute bottom-2 left-2 inline-flex h-5 items-center gap-1 rounded-full bg-black/65 px-2 text-[11px] font-medium text-white sm:h-6 sm:text-xs">
                      <Clock className="h-3 w-3" />
                      {topic.estimated_duration}
                    </span>
                    {claimed && (
                      <span className="absolute right-2 top-2 inline-flex h-5 items-center gap-1 rounded-full bg-claimed px-2 text-[11px] font-semibold text-white sm:h-6 sm:text-xs">
                        <Check className="h-3 w-3" strokeWidth={3} />
                        已领取
                      </span>
                    )}
                  </div>

                  <CardContent className="space-y-2 p-2.5 sm:space-y-2.5 sm:p-4">
                    <h3 className="line-clamp-1 text-[15px] font-semibold leading-tight text-foreground sm:text-[17px] xl:text-base">
                      {topic.topic_name}
                    </h3>
                    <div className="flex items-center gap-1.5 sm:gap-2">
                      <span
                        className="inline-flex h-5 items-center rounded-full px-2 text-[11px] font-medium sm:h-[22px] sm:text-xs"
                        style={{ background: style.tagBg, color: style.tagFg }}
                      >
                        {CONTENT_CATEGORY_LABELS[topic.content_category]}
                      </span>
                      <span className="hidden text-xs text-muted-foreground sm:inline">
                        {CONTENT_DIRECTION_LABELS[topic.content_direction]}
                      </span>
                    </div>

                    <div className={`grid gap-2 pt-0.5 ${PUBLIC_CATALOG_MODE ? 'grid-cols-1' : 'sm:grid-cols-2'}`}>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleViewDetail(topic);
                        }}
                        className={`h-9 rounded-xl border-orange-200 bg-white text-orange-800 hover:bg-orange-50 ${
                          PUBLIC_CATALOG_MODE ? '' : 'hidden sm:inline-flex'
                        }`}
                      >
                        查看详情
                      </Button>
                      {!PUBLIC_CATALOG_MODE &&
                        (claimed ? (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              openTopicResource(topic);
                            }}
                            className="h-9 rounded-xl bg-claimed-soft text-claimed hover:bg-emerald-100"
                          >
                            查看资源
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleOpenRedeem(topic);
                            }}
                            className="h-9 gap-1.5 rounded-xl bg-cta text-cta-foreground hover:bg-cta-hover"
                          >
                            <Gift className="hidden h-3.5 w-3.5 sm:block xl:hidden" />
                            立即领取
                          </Button>
                        ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {!isLoading && filteredTopics.length > 0 && (
          <div className="flex items-center justify-center gap-3 pt-6 text-[13px] text-muted-foreground">
            <span aria-hidden className="h-px w-14 bg-border" />
            已显示全部主题
            <span aria-hidden className="h-px w-14 bg-border" />
          </div>
        )}
        </div>
      </div>

      {showBackToTop && (
        <button
          type="button"
          aria-label="回到顶部"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          className="fixed bottom-6 right-4 z-30 flex h-11 w-11 items-center justify-center rounded-full bg-white text-foreground shadow-[0_6px_18px_rgba(60,32,10,0.18),0_0_0_1px_var(--border)] md:bottom-8"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}

      <TopicFilterDrawer
        open={isMobileFilterOpen}
        onOpenChange={setIsMobileFilterOpen}
        onSeeResults={() => {
          setIsMobileFilterOpen(false);
          // 等弹层关闭、页面恢复滚动后再定位；没改条件时也要回到结果开头
          window.setTimeout(scrollToResultsStart, 350);
        }}
        durationRanges={durationRanges}
        category={category}
        resultCount={filteredTopics.length}
        onDurationToggle={handleDurationFilterClick}
        onCategoryChange={setCategory}
        onClear={handleClearFilters}
        categoryCounts={categoryCounts}
        showUnclaimedToggle={!!session}
        unclaimedOnly={unclaimedOnly}
        onUnclaimedOnlyChange={setUnclaimedOnly}
      />

      {!PUBLIC_CATALOG_MODE && session && (
        <>
          <MemberBenefitsDialog
            session={session}
            hideTrigger
            open={isMemberCenterOpen}
            onOpenChange={setIsMemberCenterOpen}
            onOpenHistory={() => {
              setHistoryQuery('');
              setIsHistoryOpen(true);
            }}
            onContact={contactConfig.enabled ? () => handleContactAction('header') : undefined}
            onLogout={logout}
          />
          <MemberHistoryDialog
            passcode={session.passcode}
            hideTrigger
            open={isHistoryOpen}
            onOpenChange={setIsHistoryOpen}
            initialQuery={historyQuery}
          />
        </>
      )}
      {/* 详情弹窗：手机从底部滑上来（可下拉关闭），平板居中窄弹窗，桌面宽屏双栏（高度不超过屏幕减 60px） */}
      {isPhoneLayout ? (
        <Drawer open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DrawerContent className="border-0 bg-white data-[vaul-drawer-direction=bottom]:mt-0 data-[vaul-drawer-direction=bottom]:max-h-[96dvh] data-[vaul-drawer-direction=bottom]:rounded-t-3xl data-[vaul-drawer-direction=bottom]:border-t-0 [&>div:first-child]:mt-2.5 [&>div:first-child]:h-1.5 [&>div:first-child]:w-10">
            <DrawerClose
              aria-label="关闭"
              className="absolute right-4 top-6 z-10 flex h-11 w-11 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-accent"
            >
              <X className="h-[18px] w-[18px]" />
            </DrawerClose>
            {renderDetailBody(DrawerTitle, DrawerDescription)}
          </DrawerContent>
        </Drawer>
      ) : (
        <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
          <DialogContent
            className={`flex flex-col gap-0 overflow-hidden rounded-3xl border-0 bg-white p-0 shadow-[0_18px_60px_rgba(120,72,28,0.18)] [&>[data-slot=dialog-close]]:right-4 [&>[data-slot=dialog-close]]:top-4 [&>[data-slot=dialog-close]]:z-10 [&>[data-slot=dialog-close]]:flex [&>[data-slot=dialog-close]]:h-11 [&>[data-slot=dialog-close]]:w-11 [&>[data-slot=dialog-close]]:items-center [&>[data-slot=dialog-close]]:justify-center [&>[data-slot=dialog-close]]:rounded-full [&>[data-slot=dialog-close]]:opacity-100 [&>[data-slot=dialog-close]]:hover:bg-accent ${
              isWideDetail
                ? 'max-h-[calc(100dvh-60px)] w-[calc(100vw-2rem)] !max-w-[960px]'
                : 'max-h-[calc(100dvh-1rem)] w-[calc(100vw-1rem)] !max-w-[480px]'
            }`}
          >
            {renderDetailBody(DialogTitle, DialogDescription, isWideDetail)}
          </DialogContent>
        </Dialog>
      )}

      {/* 兑换码验证弹窗 */}
      {!PUBLIC_CATALOG_MODE && <Dialog open={isPasscodeDialogOpen} onOpenChange={(open) => {
        // 激活步骤里关闭弹窗等同于跳过订单号
        if (!open && activationStep) {
          continueAfterLogin(activationStep);
          return;
        }
        setIsPasscodeDialogOpen(open);
        if (!open) setPasscodeError('');
      }}>
        {/* 手机端贴近顶部显示，弹出键盘时输入框和按钮不会被挡住 */}
        <DialogContent className="max-w-md gap-4 rounded-2xl max-sm:top-[1rem] max-sm:max-h-[calc(100dvh-2rem)] max-sm:translate-y-0 max-sm:overflow-y-auto">
          {activationStep?.member ? (
            <>
              <DialogHeader className="sr-only">
                <DialogTitle>兑换码已激活</DialogTitle>
                <DialogDescription>选填小红书订单号，方便以后找回兑换码</DialogDescription>
              </DialogHeader>
              <OrderNoBindForm
                passcode={activationStep.member.passcode}
                member={activationStep.member}
                continueLabel={pendingRedeem ? '继续领取' : '开始使用'}
                onDone={() => continueAfterLogin(activationStep)}
              />
            </>
          ) : (<>
          <DialogHeader className="pr-8 text-left">
            <DialogTitle className="text-xl">{passcodeDialogMode === 'login' ? '会员登录' : '输入兑换码'}</DialogTitle>
            <DialogDescription>
              {passcodeDialogMode === 'login'
                ? '输入兑换码登录后，可查看会员权益与领取记录'
                : pendingRedeem
                  ? `验证后即可领取「${pendingRedeem.topic.topic_name}」`
                  : '请输入您的会员兑换码以领取主题内容'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <label htmlFor="public-passcode-input" className="text-sm font-medium text-muted-foreground">
              兑换码
            </label>
            <Input
              id="public-passcode-input"
              type="text"
              placeholder="请输入兑换码"
              value={passcode}
              onChange={(e) => {
                setPasscode(e.target.value);
                if (passcodeError) setPasscodeError('');
              }}
              aria-invalid={Boolean(passcodeError)}
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              onKeyDown={(e) => e.key === 'Enter' && handleVerifyPasscode()}
              className="h-14 rounded-xl text-center text-xl font-semibold tracking-[0.16em] placeholder:text-base placeholder:font-normal placeholder:tracking-normal focus-visible:border-cta focus-visible:ring-cta/20"
              disabled={isVerifying}
            />

            {passcodeError && (
              <p className="rounded-lg bg-red-50 px-3 py-2 text-sm leading-5 text-red-700" role="alert">
                {passcodeError}
              </p>
            )}
          </div>

          <Button
            onClick={handleVerifyPasscode}
            disabled={!passcode.trim() || isVerifying}
            className="h-12 w-full rounded-xl bg-cta text-base font-semibold text-cta-foreground hover:bg-cta-hover"
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                验证中...
              </>
            ) : (
              passcodeDialogMode === 'login' ? '登录' : '验证并领取'
            )}
          </Button>

          {contactConfig.enabled && (
            <div className="flex items-center justify-between gap-2 rounded-xl border border-dashed border-orange-200 px-3 py-1.5">
              <p className="min-w-0 flex-1 text-xs text-muted-foreground">
                {contactConfig.passcode_hint}
              </p>
              <Button
                type="button"
                variant="ghost"
                className="h-9 shrink-0 px-2 text-[13px] font-semibold text-orange-800 hover:bg-orange-50"
                onClick={() => handleContactAction('passcode')}
              >
                {contactConfig.button_text}
              </Button>
            </div>
          )}
          </>)}
        </DialogContent>
      </Dialog>}

      {/* 领取确认弹窗 */}
      {!PUBLIC_CATALOG_MODE && <Dialog open={!!confirmTopic} onOpenChange={(open) => !open && setConfirmTopic(null)}>
        <DialogContent className="max-w-md gap-4 rounded-2xl">
          <DialogHeader className="pr-8 text-left">
            <DialogTitle className="text-xl">确认领取</DialogTitle>
            <DialogDescription>
              您即将领取「{confirmTopic?.topic_name}」，请确认会员权益信息
            </DialogDescription>
          </DialogHeader>
          {confirmTopic && (
            // min-w-0：弹窗是网格布局，长主题名不加限制会把整个弹窗撑宽
            <div className="flex min-w-0 items-center gap-3">
              <div className="relative h-14 w-24 shrink-0 overflow-hidden rounded-xl bg-muted">
                {renderCover(confirmTopic, 'thumb')}
              </div>
              <div className="min-w-0">
                <p className="truncate text-base font-semibold text-foreground">{confirmTopic.topic_name}</p>
                <p className="text-[13px] text-muted-foreground">
                  {CONTENT_CATEGORY_LABELS[confirmTopic.content_category]} · 约 {confirmTopic.estimated_duration}
                </p>
              </div>
            </div>
          )}
          {session && (
            <div className="space-y-3 rounded-2xl bg-orange-50/60 p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">会员类型</span>
                <span className="inline-flex h-6 items-center rounded-full bg-orange-100 px-2.5 text-xs font-semibold text-orange-900">
                  {session.member.member_type}
                </span>
              </div>
              <QuotaChangeRow label={cycleRemainLabel} before={session.remainingCycle} />
              {!isTotalMode && <QuotaChangeRow label="今日剩余" before={session.remainingToday} />}
              {!isTotalMode && (session.member.next_cycle_borrowed ?? 0) > 0 && (
                <p className="border-t border-dashed border-orange-200 pt-3 text-[13px] leading-relaxed text-muted-foreground">
                  含预支下周期 {session.member.next_cycle_borrowed} 次，下周期只扣实际用掉的部分。
                </p>
              )}
            </div>
          )}
          <p className="text-xs leading-relaxed text-muted-foreground">
            领取后可在「领取记录」中随时再次查看，同一主题不重复扣次数。
          </p>
          <div className="grid grid-cols-[1fr_2fr] gap-2.5">
            <Button
              variant="outline"
              onClick={() => setConfirmTopic(null)}
              disabled={isRedeeming}
              className="h-12 rounded-xl border-orange-200 font-semibold text-orange-800 hover:bg-orange-50"
            >
              取消
            </Button>
            <Button
              onClick={confirmRedeem}
              disabled={isRedeeming}
              className="h-12 rounded-xl bg-cta text-base font-semibold text-cta-foreground hover:bg-cta-hover"
            >
              {isRedeeming ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  领取中...
                </>
              ) : (
                '确认领取'
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>}

      {/* 图片预览弹窗 */}
      <Dialog open={!!previewImage} onOpenChange={(open) => !open && setPreviewImage(null)}>
        <DialogContent className="max-h-[calc(100dvh-1rem)] w-fit !max-w-[calc(100vw-1rem)] overflow-y-auto border-0 bg-neutral-950 p-0 shadow-2xl sm:max-h-none sm:w-full sm:!max-w-4xl sm:overflow-visible sm:border sm:bg-white [&>button]:z-20 [&>button]:rounded-full [&>button]:bg-black/70 [&>button]:p-2 [&>button]:text-white [&>button]:opacity-100 [&>button:hover]:bg-black/90 sm:[&>button]:bg-transparent sm:[&>button]:text-neutral-900 sm:[&>button:hover]:bg-neutral-100">
          <DialogTitle className="sr-only">PPT 预览大图</DialogTitle>
          <DialogDescription className="sr-only">
            查看当前主题的完整 PPT 预览长图
          </DialogDescription>
          <div
            className="relative w-fit max-w-full bg-neutral-950 sm:flex sm:w-full sm:items-center sm:justify-center sm:bg-black/5"
            onContextMenu={(e) => e.preventDefault()}
            onDragStart={(e) => e.preventDefault()}
            style={PROTECTED_PREVIEW_STYLE}
          >
            {previewImage && (
              <>
                <img
                  src={previewImage}
                  alt="预览大图"
                  className="pointer-events-none block h-auto w-auto max-w-[calc(100vw-1rem)] select-none sm:max-h-[80vh] sm:max-w-full sm:object-contain"
                  draggable={false}
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                  style={PROTECTED_PREVIEW_STYLE}
                />
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="grid h-full w-full grid-cols-3 place-items-center gap-y-8 px-4 py-6 sm:gap-y-12 sm:px-8">
                    {VIDEO_WATERMARK_ITEMS.map((item) => (
                      <span
                        key={`image-preview-watermark-${item}`}
                        className="select-none whitespace-nowrap text-sm font-semibold tracking-[0.3em] sm:text-lg"
                        style={{
                          transform: 'rotate(-24deg)',
                          color: 'rgba(51, 65, 85, 0.24)',
                          textShadow: '0 0 2px rgba(255,255,255,0.55)',
                        }}
                      >
                        仅供预览
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* 客服弹窗 */}
      {!PUBLIC_CATALOG_MODE && <Dialog open={isContactDialogOpen} onOpenChange={setIsContactDialogOpen}>
        <DialogContent className="max-w-xs px-5 py-5">
          <DialogHeader className="items-center pb-1 text-center">
            <DialogTitle className="text-base">{contactConfig.title || '联系客服'}</DialogTitle>
            <DialogDescription className="text-xs">{contactConfig.description || '支持购买、续费、单独获取主题与找回兑换码'}</DialogDescription>
          </DialogHeader>

          <div className="space-y-3 pt-1">
            {contactConfig.wechat_id?.trim() && (
              <div className="rounded-xl border bg-muted/20 px-3 py-2.5">
                <div className="flex min-h-9 items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">微信号</p>
                    <p className="truncate font-mono text-sm text-foreground">{contactConfig.wechat_id.trim()}</p>
                  </div>
                  <Button type="button" size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={handleCopyWechatId}>
                    <Copy className="mr-1 h-3 w-3" />
                    复制
                  </Button>
                </div>
              </div>
            )}

            {contactConfig.qr_image_url?.trim() ? (
              <div className="flex flex-col items-center justify-center rounded-xl border bg-white px-3 py-3">
                <img
                  src={contactConfig.qr_image_url.trim()}
                  alt="客服二维码"
                  className="mx-auto h-36 w-36 rounded object-contain"
                />
                <p className="mt-2 text-center text-[10px] text-muted-foreground">可截图扫码添加微信</p>
              </div>
            ) : (
              <div className="rounded-xl border border-dashed bg-muted/20 p-3 text-center text-xs text-muted-foreground">
                暂未配置二维码图片
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>}

      {/* 视频预览弹窗 */}
      <Dialog open={!!previewVideo} onOpenChange={(open) => !open && setPreviewVideo(null)}>
        <DialogContent className="max-w-4xl p-0">
          <DialogTitle className="sr-only">视频预览</DialogTitle>
          <DialogDescription className="sr-only">
            播放当前主题的演讲示范视频
          </DialogDescription>
          <div className="relative flex items-center justify-center bg-black">
            {previewVideo && (
              <>
                <video
                  src={previewVideo}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  controlsList="nodownload noplaybackrate"
                  disablePictureInPicture
                  onContextMenu={(e) => e.preventDefault()}
                  className="max-h-[80vh] w-full"
                >
                  您的浏览器不支持视频播放
                </video>
                <div className="pointer-events-none absolute right-3 top-3 z-20 rounded bg-white/75 px-2 py-1 text-xs font-semibold text-slate-900/85 shadow-sm sm:right-4 sm:top-4 sm:text-sm">
                  此资源包含消音有字幕、无字幕视频
                </div>
                <div className="pointer-events-none absolute inset-0 overflow-hidden">
                  <div className="grid h-full w-full grid-cols-3 place-items-center gap-y-8 px-4 py-6 sm:gap-y-12 sm:px-8">
                    {VIDEO_WATERMARK_ITEMS.map((item) => (
                      <span
                        key={item}
                        className="select-none whitespace-nowrap text-sm font-semibold tracking-[0.3em] text-white/20 sm:text-lg"
                        style={{ transform: 'rotate(-24deg)' }}
                      >
                        仅供预览
                      </span>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// 详情弹窗的小节标题（图标 + 文字）
function DetailSectionTitle({ icon: Icon, children }: { icon: typeof Eye; children: ReactNode }) {
  return (
    <h4 className="mb-1.5 flex items-center gap-1.5 text-[15px] font-semibold text-foreground md:mb-2">
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-orange-50 text-cta">
        <Icon className="h-3.5 w-3.5" />
      </span>
      {children}
    </h4>
  );
}

// “仅供预览”水印，盖在 PPT / 文稿预览图上
function PreviewWatermark({ size = 'sm' }: { size?: 'sm' | 'lg' }) {
  return (
    <span className="pointer-events-none absolute inset-0 grid grid-cols-3 place-items-center">
      {VIDEO_WATERMARK_ITEMS.slice(0, size === 'lg' ? 6 : 3).map((item) => (
        <span
          key={item}
          className={`select-none whitespace-nowrap font-semibold tracking-[0.14em] ${size === 'lg' ? 'text-xs' : 'text-[10px]'}`}
          style={{ transform: 'rotate(-22deg)', color: 'rgba(51, 65, 85, 0.28)', textShadow: '0 0 1px rgba(255,255,255,0.55)' }}
        >
          仅供预览
        </span>
      ))}
    </span>
  );
}

// 确认领取弹窗里的“领取前 → 领取后”一行
function QuotaChangeRow({ label, before }: { label: string; before: number }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className="inline-flex items-center gap-2 text-[15px]">
        <span className="text-muted-foreground line-through">{before}</span>
        <ArrowRight className="h-4 w-4 text-muted-foreground" aria-label="领取后" />
        <strong className="text-xl text-foreground">{Math.max(0, before - 1)}</strong>
        <span className="text-muted-foreground">次</span>
      </span>
    </div>
  );
}

export default PublicTopicListPage;


